import { Link } from "react-router-dom";

const navCards = [
  {
    to: "/services",
    label: "Wealth Design",
    sub: "See how we engineer your financial architecture.",
    img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80",
    imgAlt: "Financial strategy documents on a desk",
  },
  {
    to: "/about",
    label: "About NLS",
    sub: "Meet the team behind the strategy.",
    img: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80",
    imgAlt: "Professional team in a meeting",
  },
  {
    to: "/careers",
    label: "Partner With Us",
    sub: "Build your own financial legacy with us.",
    img: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&q=80",
    imgAlt: "Energetic team collaboration",
  },
];

export default function Home() {
  return (
    <main>
      {/* ── Hero ── */}
      <section className="relative min-h-screen flex items-center justify-center text-center px-6 overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-dark/90 via-navy/85 to-navy-dark/95" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto">
          <a
            href="https://experiorfinancial.com/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 border border-gold/30 bg-gold/10 text-gold text-xs tracking-widest uppercase px-4 py-2 rounded-full mb-6 hover:bg-gold/20 transition-colors"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-gold inline-block" />
            Operating Under Experior Financial Group
          </a>
          <h1 className="font-display text-5xl md:text-7xl font-bold text-white leading-tight mb-6">
            Engineer Your Wealth.<br />
            <span className="text-gold">Eliminate the Risk.</span>
          </h1>
          <p className="text-white/60 text-lg max-w-xl mx-auto mb-10">
            Tax-free growth. 0% market floor. Living access. Built for high-income professionals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-gold">Schedule a Strategy Audit</Link>
            <Link to="/services" className="btn-outline">Explore Our Approach</Link>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/30 text-xs tracking-widest">
          <span>SCROLL</span>
          <div className="w-px h-10 bg-white/20" />
        </div>
      </section>

      {/* ── 3 Nav Image Cards ── */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          {navCards.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              className="group relative rounded-2xl overflow-hidden h-72 shadow-xl block"
              onClick={() => window.scrollTo({ top: 0, behavior: "instant" })}
            >
              <img
                src={c.img}
                alt={c.imgAlt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/90 via-navy-dark/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="font-display text-xl font-bold text-white mb-1">{c.label}</h3>
                <p className="text-white/70 text-sm mb-3">{c.sub}</p>
                <span className="text-gold text-xs tracking-widest uppercase">Learn More →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ── Private Reserve Split ── */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative rounded-2xl overflow-hidden h-[420px] shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1560472355-536de3962603?w=900&q=80"
              alt="Private financial strategy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/85 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="text-gold text-xs tracking-widest uppercase mb-1">The Private Reserve</p>
              <p className="text-white font-display text-lg font-semibold">Your capital — growing tax-free, every day.</p>
            </div>
          </div>
          <div>
            <p className="text-gold text-xs tracking-widest uppercase mb-3">Our Solution</p>
            <h2 className="section-title">Max-Funded IUL</h2>
            <span className="gold-line" />
            <p className="text-gray-600 leading-relaxed mb-8">
              A properly structured Max-Funded IUL is the most powerful tax-advantaged vehicle available — market-linked growth with a guaranteed 0% floor, tax-free access, and living benefits built in.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-8">
              {[
                { label: "0% Floor", sub: "Never loses to a down market." },
                { label: "Tax-Free Growth", sub: "No tax event on accumulation." },
                { label: "Living Benefits", sub: "Access your value while alive." },
                { label: "Private Reserve", sub: "Your own capital structure." },
              ].map((p) => (
                <div key={p.label} className="card-dark">
                  <p className="font-display text-gold text-lg font-bold mb-1">{p.label}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{p.sub}</p>
                </div>
              ))}
            </div>
            <Link to="/services" className="btn-gold">See the Architecture</Link>
          </div>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <section className="py-16 px-6 bg-gray-50 border-y border-gray-200">
        <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
          {[
            { stat: "360°", label: "View of Your Financial Health" },
            { stat: "0%", label: "Market Floor — Guaranteed" },
            { stat: "100%", label: "Focus on Living Benefits" },
          ].map((s) => (
            <div key={s.stat} className="border border-gold/30 rounded-xl p-8">
              <p className="font-display text-5xl font-bold text-gold mb-2">{s.stat}</p>
              <p className="text-gray-600 text-sm tracking-wide">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Network ── */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-gold text-xs tracking-widest uppercase mb-3">Our Network</p>
            <h2 className="section-title">Backed by Institutional Strength</h2>
            <span className="gold-line mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <a href="https://experiorfinancial.com/" target="_blank" rel="noreferrer" className="card-dark group hover:border-gold/60">
              <p className="text-gold text-xs tracking-widest uppercase mb-2">Premier North American IMO</p>
              <h3 className="font-display text-xl font-bold text-navy mb-2 group-hover:text-gold transition-colors">Experior Financial Group</h3>
              <p className="text-gray-600 text-sm mb-4">World-class carrier relationships and product access backed by institutional stability.</p>
              <span className="text-gold text-xs">experiorfinancial.com →</span>
            </a>
            <a href="https://associateownersgroup.com/" target="_blank" rel="noreferrer" className="card-dark group hover:border-gold/60">
              <p className="text-gold text-xs tracking-widest uppercase mb-2">Global IMO Alliance · IPO-Ready</p>
              <h3 className="font-display text-xl font-bold text-navy mb-2 group-hover:text-gold transition-colors">Associate Owners Group (AOG)</h3>
              <p className="text-gray-600 text-sm mb-4">IPO-targeted global scale — positioning clients at the forefront of financial innovation.</p>
              <span className="text-gold text-xs">associateownersgroup.com →</span>
            </a>
          </div>
        </div>
      </section>

      {/* ── CTA Banner with image ── */}
      <section className="relative py-32 px-6 overflow-hidden text-center">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-navy-dark/85" />
        </div>
        <div className="relative max-w-2xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">Ready to Architect Your Wealth?</h2>
          <p className="text-white/55 mb-8">No products. No pressure. Just strategy.</p>
          <Link to="/contact" className="btn-gold text-base px-10 py-4">
            Request My Strategy Audit
          </Link>
        </div>
      </section>
    </main>
  );
}
